import { Baby, Ban, Building2, CalendarClock, CircleAlert, ShieldAlert, Thermometer, UserX, Wallet, type LucideIcon } from 'lucide-react';

export const AppointmentCancellationReason: {
    ARRIVAL: 'ARRIVAL',
    HEALTH: 'HEALTH',
    OTHER_CLINIC: 'OTHER_CLINIC',
    SCHEDULING_CONFLICT: 'SCHEDULING_CONFLICT',
    NO_LONGER_NEEDED: 'NO_LONGER_NEEDED',
    FINANCIAL: 'FINANCIAL',
    TREATMENT_ANXIETY: 'TREATMENT_ANXIETY',
    PREGNANCY_OR_BREASTFEEDING: 'PREGNANCY_OR_BREASTFEEDING',
    BOOKING_ERROR: 'BOOKING_ERROR',
    PRACTITIONER_UNAVAILABLE: 'PRACTITIONER_UNAVAILABLE',
    CANCELLED_BY_CLINIC: 'CANCELLED_BY_CLINIC',
    OTHER: 'OTHER',
} = {
    ARRIVAL: 'ARRIVAL',
    HEALTH: 'HEALTH',
    OTHER_CLINIC: 'OTHER_CLINIC',
    SCHEDULING_CONFLICT: 'SCHEDULING_CONFLICT',
    NO_LONGER_NEEDED: 'NO_LONGER_NEEDED',
    FINANCIAL: 'FINANCIAL',
    TREATMENT_ANXIETY: 'TREATMENT_ANXIETY',
    PREGNANCY_OR_BREASTFEEDING: 'PREGNANCY_OR_BREASTFEEDING',
    BOOKING_ERROR: 'BOOKING_ERROR',
    PRACTITIONER_UNAVAILABLE: 'PRACTITIONER_UNAVAILABLE',
    CANCELLED_BY_CLINIC: 'CANCELLED_BY_CLINIC',
    OTHER: 'OTHER',
};

export type AppointmentCancellationReason = (typeof AppointmentCancellationReason)[keyof typeof AppointmentCancellationReason];

// Must stay in sync with CUSTOMER_CANCELLATION_REASONS on the server, which rejects any other code
export type ReasonLabel = { de: string; en: string };

export const CUSTOMER_CANCELLATION_REASON_LABELS = {
    [AppointmentCancellationReason.HEALTH]: { de: 'Gesundheitlich verhindert (krank, anderweitige medizinische Gründe )', en: 'Health reasons (ill or other medical reasons)' },
    [AppointmentCancellationReason.OTHER_CLINIC]: { de: 'Termin bei anderer Klinik gebucht', en: 'Booked an appointment at another clinic' },
    [AppointmentCancellationReason.SCHEDULING_CONFLICT]: { de: 'Terminkonflikt - Beruf, Familie, Betreuung', en: 'Scheduling conflict - work, family, childcare' },
    [AppointmentCancellationReason.NO_LONGER_NEEDED]: { de: 'Termin nicht mehr nötig / Kein Behandlungswunsch mehr', en: 'Appointment no longer needed / no longer want the treatment' },
    [AppointmentCancellationReason.FINANCIAL]: { de: 'Finanzielle Gründe', en: 'Financial reasons' },
    [AppointmentCancellationReason.TREATMENT_ANXIETY]: { de: 'Unsicherheit oder Angst vor der Behandlung', en: 'Uncertainty or anxiety about the treatment' },
    [AppointmentCancellationReason.PREGNANCY_OR_BREASTFEEDING]: { de: 'Mögliche Schwangerschaft oder Stillzeit', en: 'Possible pregnancy or breastfeeding' },
    [AppointmentCancellationReason.BOOKING_ERROR]: { de: 'Fehlerhafte Buchung (z.B. falsche Leistung, Discount Code vergessen)', en: 'Booking error (e.g. wrong treatment, forgot discount code)' },
    [AppointmentCancellationReason.PRACTITIONER_UNAVAILABLE]: { de: 'Nicht bei Wunschbehandler/in eingebucht bzw. Wunschbehandler/in nicht verfügbar', en: 'Not booked with preferred practitioner / Preferred practitioner not available' },
} as const satisfies Record<string, ReasonLabel>;

// Must stay in sync with CUSTOMER_MOVE_REASONS on the server, which rejects any other code
export const CUSTOMER_MOVE_REASON_LABELS = {
    [AppointmentCancellationReason.HEALTH]: { de: 'Gesundheitlich verhindert (krank, anderweitige medizinische Gründe )', en: 'Health reasons (ill or other medical reasons)' },
    [AppointmentCancellationReason.SCHEDULING_CONFLICT]: { de: 'Terminkonflikt - Beruf, Familie, Betreuung', en: 'Scheduling conflict - work, family, childcare' },
} as const satisfies Record<string, ReasonLabel>;

export type CustomerCancellationReason = keyof typeof CUSTOMER_CANCELLATION_REASON_LABELS;
export type CustomerMoveReason = keyof typeof CUSTOMER_MOVE_REASON_LABELS;

export const CUSTOMER_REASON_ICONS: Record<CustomerCancellationReason | CustomerMoveReason, LucideIcon> = {
    [AppointmentCancellationReason.SCHEDULING_CONFLICT]: CalendarClock,
    [AppointmentCancellationReason.HEALTH]: Thermometer,
    [AppointmentCancellationReason.NO_LONGER_NEEDED]: Ban,
    [AppointmentCancellationReason.OTHER_CLINIC]: Building2,
    [AppointmentCancellationReason.PRACTITIONER_UNAVAILABLE]: UserX,
    [AppointmentCancellationReason.TREATMENT_ANXIETY]: ShieldAlert,
    [AppointmentCancellationReason.PREGNANCY_OR_BREASTFEEDING]: Baby,
    [AppointmentCancellationReason.FINANCIAL]: Wallet,
    [AppointmentCancellationReason.BOOKING_ERROR]: CircleAlert,
};
